import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import MarginField from '@/components/margin/MarginField';
import { useCatalogue, useCreateProductMarginFilter, useUpdateFilterPack } from '@/services/api/hooks/catalog';
import { CatalogueProvider } from '@/services/catalogue/CatalogueService';
import {
  AdminCatalogueConfiguration,
  CompanyCatalogueConfiguration,
  PartnerCatalogueConfiguration
} from '@/services/catalogue/catalogue';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import LocalizedStrings from 'react-localization';
import { z } from 'zod';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';

const strings = new LocalizedStrings({
  en: {
    productMargin: 'Product margin',
    description:
      'Product margin is the specific margin applied to a single product. This margin will take priority over other settings like criteria or margin filters.',
    margin: 'Margin'
  }
});

const ProductMarginModalFields = z.object({
  value: z.string()
});

type ProductMarginModalFields = z.infer<typeof ProductMarginModalFields>;

export const ProductMarginModal: FC<
  ModalProps<{
    initialValue: number;
    productId: string;
    configuration: CompanyCatalogueConfiguration | PartnerCatalogueConfiguration | AdminCatalogueConfiguration;
  }>
> = props => {
  const initialValue = props.props?.initialValue;
  const productId = props.props?.productId;
  const configuration = props.props?.configuration;

  const ManageProductMarginForm = () => {
    const [catalog] = useCatalogue(configuration!);
    const updateFilterPack = useUpdateFilterPack();
    const createFilter = useCreateProductMarginFilter();

    const methods = useForm<ProductMarginModalFields>({
      resolver: zodResolver(ProductMarginModalFields),
      defaultValues: { value: initialValue ? String(initialValue) : undefined }
    });

    const onSubmit = async (data: ProductMarginModalFields) => {
      const filter = await createFilter.mutateAsync({
        value: Number(data.value),
        id: productId!
      });
      catalog!.filterPack.marginFilters.push(filter);
      await updateFilterPack.mutateAsync({
        ...catalog!.filterPack,
        marginFilterIds: catalog!.filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: catalog!.filterPack.productFilters.map(filter => filter.id),
        globalMarginModifier: catalog!.filterPack.globalMarginModifier
      });
      props.close();
    };

    return (
      <Form methods={methods} onSubmit={onSubmit} className="flex w-full flex-col gap-4 py-2">
        <MarginField id="value" />
        <Button
          loading={updateFilterPack.isLoading}
          className="w-fit place-self-end"
          variant={ButtonVariant.Primary}
          type="submit"
        >
          Update
        </Button>
      </Form>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="flex w-full flex-col gap-2 p-10 pb-12">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">{strings.productMargin}</h1>
        </Modal.Controls>
        <p className="mt-0.5 leading-tight">{strings.description}</p>

        <Suspense fallback={<Loading />}>
          <CatalogueProvider configuration={configuration!}>
            <ManageProductMarginForm />
          </CatalogueProvider>
        </Suspense>
      </Card>
    </Modal>
  );
};
